import Image from "next/image"
import Link from "next/link"
import { ArrowRight, Quote, Sparkles } from "lucide-react"

import { MotionArticle, MotionDiv, MotionList, MotionSection, blurIn, fadeInLeft, fadeInRight, scaleIn } from "@/components/motion"
import { AmbientGlow, LiftCard } from "@/components/motion-primitives"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import type { BlogPost, CaseStudy, CmsCta, Testimonial } from "@/lib/cms"
import { cn } from "@/lib/utils"

export type BlogCollectionSectionProps = {
  eyebrow?: string
  title: string
  description?: string
  posts: BlogPost[]
  cta?: CmsCta
  className?: string
}

export type TestimonialWallProps = {
  eyebrow?: string
  title: string
  description?: string
  testimonials: Testimonial[]
  className?: string
}

export type CaseStudyCollectionSectionProps = {
  eyebrow?: string
  title: string
  description?: string
  caseStudies: CaseStudy[]
  cta?: CmsCta
  className?: string
}

export type CollectionProofStripProps = {
  items: { value: string; label: string }[]
  className?: string
}

/** CMS-ready sections for blogs, testimonials, case studies, and proof collections. */
export function BlogCollectionSection({ eyebrow, title, description, posts, cta, className }: BlogCollectionSectionProps) {
  return (
    <MotionSection className={cn("bg-background py-24 sm:py-32", className)}>
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv variants={fadeInLeft} className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <div className="max-w-3xl">
            {eyebrow && <Badge variant="outline" className="mb-5 rounded-full px-3 py-1 text-primary">{eyebrow}</Badge>}
            <h2 className="font-heading text-4xl font-semibold tracking-tight text-foreground sm:text-6xl">{title}</h2>
            {description && <p className="mt-5 text-lg leading-8 text-muted-foreground">{description}</p>}
          </div>
          {cta && (
            <Button asChild variant="outline" size="lg" className="group shrink-0 rounded-full bg-background/70">
              <Link href={cta.href}>
                {cta.label}
                <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Button>
          )}
        </MotionDiv>

        <MotionList className="mt-12 grid gap-5 md:grid-cols-3">
          {posts.map((post, index) => (
            <MotionArticle key={post.slug} variants={blurIn} className={cn("group", index === 0 && "md:col-span-2")}>
              <Link href={`/blog/${post.slug}`} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4">
                <Card className="h-full overflow-hidden rounded-panel border-border/70 bg-card shadow-[var(--shadow-card)] transition-all duration-500 group-hover:-translate-y-1 group-hover:shadow-[0_26px_80px_hsl(var(--foreground)/0.14)]">
                  {post.image && (
                    <div className={cn("relative overflow-hidden bg-muted", index === 0 ? "h-72" : "h-56")}>
                      <Image src={post.image.src} alt={post.image.alt} fill sizes={index === 0 ? "(min-width: 768px) 66vw, 100vw" : "(min-width: 768px) 33vw, 100vw"} className="object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground/30 via-transparent to-transparent" />
                    </div>
                  )}
                  <div className="p-6 sm:p-7">
                    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                      <span className="text-primary">{post.category}</span>
                      <span>{post.readingTime}</span>
                    </div>
                    <h3 className="font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">{post.title}</h3>
                    <p className="mt-4 leading-7 text-muted-foreground">{post.excerpt}</p>
                    <p className="mt-6 text-sm font-medium text-primary">{post.publishedAt}</p>
                  </div>
                </Card>
              </Link>
            </MotionArticle>
          ))}
        </MotionList>
      </div>
    </MotionSection>
  )
}

export function TestimonialWall({ eyebrow, title, description, testimonials, className }: TestimonialWallProps) {
  return (
    <MotionSection className={cn("relative isolate overflow-hidden bg-foreground py-24 text-background sm:py-32", className)}>
      <div className="absolute inset-0 -z-10 animate-gradient-shift bg-[radial-gradient(circle_at_16%_20%,hsl(var(--primary-glow)/0.24),transparent_30%),radial-gradient(circle_at_90%_72%,hsl(var(--accent)/0.2),transparent_28%)] bg-[length:140%_140%]" />
      <AmbientGlow className="-left-32 top-1/4 h-[26rem] w-[26rem] bg-primary/20" duration={9} />
      <AmbientGlow className="right-[-10rem] bottom-[-4rem] h-[22rem] w-[22rem] bg-accent/16" duration={11} />
      <div className="mx-auto grid w-full max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.78fr_1.22fr] lg:px-8">
        <MotionDiv variants={fadeInLeft} className="lg:sticky lg:top-24 lg:self-start">
          {eyebrow && <Badge className="mb-5 rounded-full border-background/15 bg-background/10 px-3 py-1 text-background">{eyebrow}</Badge>}
          <h2 className="font-heading text-4xl font-semibold tracking-tight sm:text-6xl">{title}</h2>
          {description && <p className="mt-5 text-lg leading-8 text-background/68">{description}</p>}
        </MotionDiv>

        <MotionList className="grid gap-4 md:grid-cols-2">
          {testimonials.map((testimonial, index) => (
            <MotionDiv key={`${testimonial.name}-${index}`} variants={scaleIn}>
              <LiftCard lift={6} scale={1.02}>
                <Card className="h-full rounded-panel border-background/10 bg-background/10 p-7 text-background shadow-none backdrop-blur transition-all duration-500 hover:bg-background/14">
                  <Quote className="mb-7 size-8 text-accent" />
                  <blockquote className="text-xl font-medium leading-8 tracking-tight">&ldquo;{testimonial.quote}&rdquo;</blockquote>
                  <figcaption className="mt-8 border-t border-background/10 pt-5 text-sm text-background/68">
                    <span className="font-semibold text-background">{testimonial.name}</span>
                    {testimonial.role && <span> / {testimonial.role}</span>}
                    {testimonial.company && <span>, {testimonial.company}</span>}
                  </figcaption>
                </Card>
              </LiftCard>
            </MotionDiv>
          ))}
        </MotionList>
      </div>
    </MotionSection>
  )
}

export function CaseStudyCollectionSection({ eyebrow, title, description, caseStudies, cta, className }: CaseStudyCollectionSectionProps) {
  return (
    <MotionSection className={cn("bg-muted/46 py-24 sm:py-32", className)}>
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv variants={fadeInLeft} className="max-w-3xl">
          {eyebrow && (
            <Badge variant="secondary" className="mb-5 gap-2 rounded-full px-3 py-1 text-primary">
              <Sparkles className="size-3.5" />
              {eyebrow}
            </Badge>
          )}
          <h2 className="font-heading text-4xl font-semibold tracking-tight text-foreground sm:text-6xl">{title}</h2>
          {description && <p className="mt-5 text-lg leading-8 text-muted-foreground">{description}</p>}
        </MotionDiv>

        <MotionList className="mt-12 grid gap-5 lg:grid-cols-2">
          {caseStudies.map((caseStudy) => (
            <MotionDiv key={caseStudy.slug} variants={fadeInRight}>
              <Link href={`/case-studies/${caseStudy.slug}`} className="group block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4">
                <Card className="grid h-full overflow-hidden rounded-panel border-border/70 bg-card shadow-[var(--shadow-card)] transition-all duration-500 group-hover:-translate-y-1 group-hover:shadow-[0_26px_80px_hsl(var(--foreground)/0.14)] md:grid-cols-[0.9fr_1.1fr]">
                  {caseStudy.image && (
                    <div className="relative min-h-72 overflow-hidden bg-muted">
                      <Image src={caseStudy.image.src} alt={caseStudy.image.alt} fill sizes="(min-width: 1024px) 45vw, 100vw" className="object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="flex min-h-72 flex-col justify-between p-7">
                    <div>
                      <div className="mb-5 flex flex-wrap items-center gap-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                        {caseStudy.industry && <span>{caseStudy.industry}</span>}
                        {caseStudy.result && <span className="text-primary">{caseStudy.result}</span>}
                      </div>
                      <h3 className="font-heading text-3xl font-semibold tracking-tight text-foreground">{caseStudy.title}</h3>
                      <p className="mt-4 leading-7 text-muted-foreground">{caseStudy.summary}</p>
                    </div>
                    <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                      View story
                      <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Card>
              </Link>
            </MotionDiv>
          ))}
        </MotionList>

        {cta && (
          <MotionDiv className="mt-10">
            <Button asChild size="lg" className="group rounded-full">
              <Link href={cta.href}>
                {cta.label}
                <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Button>
          </MotionDiv>
        )}
      </div>
    </MotionSection>
  )
}

export function CollectionProofStrip({ items, className }: CollectionProofStripProps) {
  return (
    <MotionSection className={cn("border-y border-border/70 bg-card py-8", className)}>
      <MotionList className="mx-auto grid w-full max-w-7xl gap-4 px-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-4 lg:px-8">
        {items.map((item) => (
          <MotionDiv key={item.label} variants={scaleIn} className="rounded-card bg-background p-5 shadow-sm">
            <dt className="text-sm leading-6 text-muted-foreground">{item.label}</dt>
            <dd className="mt-2 font-heading text-3xl font-semibold tracking-tight text-foreground">{item.value}</dd>
          </MotionDiv>
        ))}
      </MotionList>
    </MotionSection>
  )
}